<template>
  <div class="knowledge-progress">
    <v-row class="v-knowledge-progress">
      <v-col align="center" class="flex-grow-1 col-xs-12 col-md-12 col-lg-3">
        <v-card
          class="v-knowledge-progress__v-avatar border py-5 px-3"
          height="100%"
        >
          <img
            id="knowledge-avatar"
            class="knowledge-avatar"
            :src="getKnowledgeAvatarAsset()"
          />
          <stars :score="numberOfStars" :maxScore="3" />
          <v-col align-self="center">
            <h3>{{ knowledgeLevelName }}</h3>
          </v-col>
          <patient-test-summary
            :patientTestSummary="this.patientTestSummary"
            :levelColor="knowledgeLevelColor"
          />
        </v-card>
      </v-col>
      <v-col class="col-xs-12 col-md-12 col-lg-9">
        <v-row class="">
          <v-col
            class="col-xs-12 col-md-12 col-lg-6"
            style="padding-top: 0 !important"
          >
            <v-row class="border mr-0 py-3">
              <v-col cols="3" align-self="center" align="center">
                <eva-icon
                  name="bulb-outline"
                  class="v-knowledge-progress__informatic-icon"
                ></eva-icon>
              </v-col>
              <v-col
                cols="9"
                align-self="center"
                v-if="this.showHintForImprovementKnowledge"
              >
                {{
                  $ucfirst(
                    $t("knowledge.learnMoreAndImprove", {
                      params: { domainsLowScore: this.improveKnowledgeDomains },
                    })
                  )
                }}
              </v-col>
              <v-col
                cols="9"
                align-self="center"
                v-if="!this.showHintForImprovementKnowledge"
              >
                {{ $ucfirst($t("knowledge.improveByWatchingVideos")) }}
              </v-col>
            </v-row>
          </v-col>
          <v-col
            class="col-xs-12 col-md-12 col-lg-6"
            style="padding-top: 0 !important"
          >
            <v-row class="border py-3 knowledge-progress__videoscounter-box">
              <v-col cols="3" align-self="center" align="center">
                <eva-icon
                  :name="
                    amountOfVideosWatched ? 'eye-outline' : 'eye-off-outline'
                  "
                  class="v-knowledge-progress__informatic-icon"
                ></eva-icon>
                <span
                  class="knowledge-progress__videoscounter"
                  v-if="amountOfVideosWatched"
                  >{{ amountOfVideosWatched }}</span
                >
              </v-col>
              <v-col cols="9" align-self="center">
                {{ $ucfirst($t("knowledge.areYouWatchingTheVideos")) }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="border pa-8 col-xs-12">
          <v-col align-self="center">
            <h3>{{ $ucfirst($t("knowledge.myKnowledgeProgress")) }}</h3>
          </v-col>
          <v-col>
            <v-row>
              <v-col>
                <h6 class="text-label">
                  {{ $ucfirst($t("general.from", { catalog: "app_mobile" })) }}
                </h6>
                <date-picker
                  v-model="graphStartDate"
                  :datePickerAttr="{
                    min: visit1Date.format('YYYY-MM-DD'),
                    max: this.moment(graphEndDate).format('YYYY-MM-DD'),
                  }"
                  :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
                  :hideMessageFromDate="visit1Date.format('YYYY-MM-DD')"
                  :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
                />
              </v-col>
              <v-col>
                <h6 class="text-label">
                  {{ $ucfirst($t("general.until", { catalog: "app_mobile" })) }}
                </h6>
                <date-picker
                  v-model="graphEndDate"
                  alignRight
                  :datePickerAttr="{
                    min: this.moment(graphStartDate).format('YYYY-MM-DD'),
                    max: this.moment().format('YYYY-MM-DD'),
                  }"
                  :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
                  :hideMessageFromDate="visit1Date.format('YYYY-MM-DD')"
                  :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <apexchart
              height="300"
              type="bar"
              :options="chartOptions"
              :series="chartSeries"
            />
          </v-col>
          <v-col cols="12">
            <div class="d-flex justify-center ml-10">
              <div
                v-for="legend in legends"
                class="c-timeline__legend mx-2"
                :key="legend.name"
              >
                <v-icon small :color="legend.color">mdi-circle</v-icon>
                <p class="mb-0 ml-1">{{legend.name}}</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import gql from "graphql-tag";
import Moment from "moment";
import { extendMoment } from "moment-range";
import Stars from "../../../components/Parts/Stars";
import PatientTestSummary from "../../../components/Parts/PatientTestSummary";
import DatePicker from "../../../components/Parts/DatePicker";
import { mapGetters } from "vuex";

/**
 * Component PatientOverviewKnowledgeProgress.vue
 *
 * @module PatientOverviewKnowledgeProgress
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.8] app/src/APP_DASHBOARD/templates/views/Patient/Knowledge/PatientOverviewKnowledgeProgress.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const moment = extendMoment(Moment);

export default {
  name: "PatientOverviewKnowledgeProgress",
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
      knowledgeLevel: "patientSummary/knowledgeLevelKey",
      knowledgeLevelName: "patientSummary/knowledgeLevelName",
    }),
    knowledgeLevelColor: function () {
      return this.$app.knowledgeManager.getColorForKnowledgeLevel(
        this.$store.getters["patientSummary/knowledgeLevel"].level
      );
    },
    visit1Date() {
      const v1 = this.$store.getters["patientSummary/visit1Date"];
      return v1 ? moment(v1) : moment();
    },
    moment: () => moment
  },
  components: {
    Stars,
    PatientTestSummary,
    DatePicker,
  },
  data: function () {
    return {
      graphStartDate: moment().format("YYYY-MM-DD"),
      graphEndDate: moment().format("YYYY-MM-DD"),
      numberOfStars: 0,
      patientTestSummary: [],
      amountOfVideosWatched: null,
      showHintForImprovementKnowledge: false,
      improveKnowledgeDomains: [],
      starsPerBar: [],
      loadChartCount: 0,
      legends: [],
      chartOptions: {
        chart: {
          toolbar: {
            show: false,
          },
          id: "my-knowledge-progress",
          type: "bar",
        },
        yaxis: {
          title: {
            text: this.$ucfirst(this.$t('general.score', {catalog: 'app_mobile'})),
            style: {
              fontSize: '0.85rem'
            },
            offsetX: -15
          },
          labels: {
            style: {
              fontSize: '0.75rem',
            },
            minWidth: 80,
          },
          max: 14,
          tickAmount: 7
        },
        xaxis: {
          title: {
            text: this.$ucfirst(this.$t("alerts.time")),
            style: {
              fontSize: '0.85rem'
            },
            offsetY: 5
          },
          labels: {
            minHeight: 90,
            style: {
              fontSize: '0.75rem',
            }
          }
        },
        tooltip: {
          style: {
            fontSize: '0.85rem',
          },
        },
        legend: {
          show: false,
        },
        plotOptions: {
          bar: {
            distributed: true,
            dataLabels: {
              position: "top",
            },
          },
        },
        colors: ["#0000FF"],
        dataLabels: {
          enabled: true,
        },
        noData: {
          text: this.$t("noData"),
          align: "center",
          verticalAlign: "middle",
          offsetX: 0,
          offsetY: -32,
          style: {
            // color: this.$color.coro["primary-500"],
            fontSize: "22px",
            fontFamily: "Futura",
          },
        },
      },
      chartSeries: [
        {
          name: this.$ucfirst(this.$t('general.score', {catalog: 'app_mobile'})),
          data: [],
        },
      ],
      defaultDomainKeys: [
        'HEALTHPARAMETERS',
        'HEALTHYNUTRITION',
        'MEDICATIONS',
        'MYHEART',
        'SMOKECESSATION',
        'STARTMOVING',
        'STRESSMANAGEMENT'
      ]
    };
  },
  watch: {
    patientId: function () {
      this.loadData();
    },
    graphStartDate: function () {
      this.loadChartData();
    },
    graphEndDate: function () {
      this.loadChartData();
    },
  },
  mounted: function () {
    const start = this.$store.getters["patientSummary/lastCompletedSDMEncounterDate"];
    if (start.isValid()) {
      this.graphStartDate = start.format("YYYY-MM-DD")
    }
    this.loadData();
  },
  methods: {
    loadData: function () {
      this.loadAmountOfWatchedVideos();
      this.loadPatientTestSummary();
      this.loadNumberOfStars();
      this.loadChartData();
    },

    getKnowledgeAvatarAsset: function () {
      switch(this.knowledgeLevel.toLowerCase()) {
        case 'beginner':
          return this.$asset('image/beginner.png');
        case 'advanced':
          return this.$asset('image/advanced.png');
        case 'expert':
          return this.$asset('image/expert.png');
        default:
          return this.$asset('image/beginner.png');
      }
    },

    getDomainName(key) {
      switch(key) {
        case 'SMOKECESSATION':
          return this.$ucfirst(this.$t('behaviourComponentJourney.SMOKECESSATION'))
        case 'STARTMOVING':
          return this.$ucfirst(this.$t('behaviourComponentJourney.STARTMOVING'))
        case 'HEALTHYNUTRITION':
          return this.$ucfirst(
              this.$t(
                  'knowledge.questions.myheart.advanced.healthprogramtarget.answers.nutrition',
                  {catalog: 'content'}
              )
          )
        case 'MEDICATIONS':
          return this.$ucfirst(this.$t('medication.medicationAdherence'))
        case 'STRESSMANAGEMENT':
          return this.$ucfirst(this.$t('behaviourComponentJourney.STRESSMANAGEMENT'))
        case 'HEALTHPARAMETERS':
          return this.$ucfirst(this.$t('screens.myHealthParametersSettingsScreen', {catalog: 'app_mobile'}))
        case 'MYHEART':
          return this.$ucfirst(this.$t('categoryType.myHeart', {catalog: 'app_mobile'}))
        default:
          return key;
      }
    },

    getKnowledgeLevelName(level) {
      switch(parseInt(level)) {
        case 1:
          return this.$ucfirst(this.$t('knowledge.level.beginner', {catalog: 'content'}));
        case 2:
          return this.$ucfirst(this.$t('knowledge.level.advanced', {catalog: 'content'}));
        case 3:
          return this.$ucfirst(this.$t('knowledge.level.expert', {catalog: 'content'}));
        default:
          return '';
      }
    },

    loadAmountOfWatchedVideos: function () {
      this.$app.apolloClient
        .query({
          query: gql`
            query getAmountOfWatchedVideos($patientId: String!) {
              contentPatientEventSummary(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  gate: and
                  inputs: [
                    { field: eventType, value: "opened" }
                    { field: categoryKey, value: "EDUCATION" }
                    { field: categoryKey, value: "VIDEO" }
                    { function: sinceLastSDMEncounter }
                  ]
                }
              ) {
                count
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          this.amountOfVideosWatched = result.data.contentPatientEventSummary[0]
            ? result.data.contentPatientEventSummary[0].count
            : 0;
        });
    },

    loadPatientTestSummary: function () {
      this.$app.knowledgeManager
        .getPatientTestSummary(this.patientId, this.knowledgeLevel)
        .then((res) => {
          // Set default values if empty
          if (!res.length) {
            const defaultScores = this.defaultDomainKeys.map(key => ({groupKey0: key, scorePercent: 0}));
            this.patientTestSummary = defaultScores;
            return;
          }

          // Sort by best (highest) scorePercent first.
          let sorted = Object.values(res).sort(function (a, b) {
            a = a.scorePercent;
            b = b.scorePercent;

            return a > b ? -1 : a < b ? 1 : 0;
          });

          // console.log(sorted);
          this.patientTestSummary = sorted;

          // Set knowledge domains to improve.
          let knowledgeDomainsToLearnMoreAbout = [];

          sorted.map((val, index) => {
            if (parseFloat(val.scorePercent) < 0.5) {
              this.showHintForImprovementKnowledge = true;
              knowledgeDomainsToLearnMoreAbout.push(
                " " + this.getDomainName(val.groupKey0)
              );
            }
          });

          this.improveKnowledgeDomains = knowledgeDomainsToLearnMoreAbout;
        });
    },

    loadNumberOfStars: function () {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getStars($patientId: String!) {
              myPatientTests(
                functions: { function: patientId, parameters: [$patientId] }
                limit: 1
                orders: [{ field: completionDate, direction: desc }]
                conditions: { field: completionDate, operator: notnull }
              ) {
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                  startCursor
                  endCursor
                }
                edges {
                  node {
                    id
                    numberOfStars
                    knowledgeLevel {
                      key
                      name
                      level
                    }
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          this.numberOfStars = result.data.myPatientTests.edges.length
            ? result.data.myPatientTests.edges[0].node.numberOfStars
            : 0;
        });
    },

    loadChartData: function () {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myPatientTests(
              $startDate: String!
              $endDate: String!
              $patientId: String!
            ) {
              myPatientTests(
                functions: { function: patientId, parameters: [$patientId] }
                orders: [{ field: completionDate, direction: asc }]
                conditions: {
                  field: completionDate
                  operator: between_day
                  parameters: [$startDate, $endDate]
                }
              ) {
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                  startCursor
                  endCursor
                }
                edges {
                  node {
                    id
                    isCompleted
                    completionDate
                    numberOfAnsweredQuestions
                    numberOfCorrectQuestions
                    numberOfWrongQuestions
                    numberOfStars
                    scorePercent
                    knowledgeLevel {
                      key
                      name
                      level
                    }
                  }
                }
              }
            }
          `,
          variables: {
            startDate: moment(this.graphStartDate).format('YYYY-MM-DD HH:mm:ss'),
            endDate: moment(this.graphEndDate).add(1, 'days').subtract(1, 'seconds').format('YYYY-MM-DD HH:mm:ss'),
            patientId: this.patientId,
          },
        })
        .then((result) => {
          let newData = [];
          let newStarsPerBar = [];
          let newColors = [];
          let knowledgeLevels = [];

          result.data.myPatientTests.edges.map((edge, index) => {
            let e = edge.node;

            if (e.isCompleted) {
              // console.log(e);

              let date = moment(e.completionDate).format("DD/MM");

              newData.push({
                x: date,
                y: e.numberOfCorrectQuestions,
              });

              // Bar-color per knowledgelevel
              newColors.push(
                this.$app.knowledgeManager.getColorForKnowledgeLevel(
                  e.knowledgeLevel.level
                )
              );

              // Amount of stars per bar
              newStarsPerBar.push(e.numberOfStars);

              // Knowledge levels
              if (!knowledgeLevels.includes(e.knowledgeLevel.level)) {
                knowledgeLevels.push(e.knowledgeLevel.level)
              }
            }
          });

          this.legends = knowledgeLevels.map(level => {
            return {
              name: this.getKnowledgeLevelName(level),
              color: this.$app.knowledgeManager.getColorForKnowledgeLevel(level)
            }
          });

          this.starsPerBar = newStarsPerBar;
          this.chartSeries = [
            {
              data: newData,
            },
          ];

          this.chartOptions = {
            ...this.chartOptions,
            chart: {
              animations: {
                enabled: this.loadChartCount > 1,
              },
            },
            colors: newColors,
            dataLabels: {
              enabled: true,
              offsetY: 8,
              formatter: function (val, opt) {
                let res = "";
                for (let i = 0; i < newStarsPerBar[opt.dataPointIndex]; i++) {
                  res += "✭";
                }

                return res;
              },
            },
          };

          this.loadChartCount++;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../../../../../scss/colors";

.v-knowledge-progress {
  .knowledge-avatar {
    margin-top: 16px;
    width: 128px;
    height: 128px;
    border-radius: 128px !important;
  }

  .v-knowledge-progress__label {
    font-size: 12px;
  }

  .v-knowledge-progress__informatic-icon {
    border: 1px solid map-get($coroColors, lightgray2);
    padding: 16px 16px 8px 16px;
    border-radius: 6px;
  }

  .v-knowledge-progress__v-avatar {
    .v-avatar {
      border-radius: 50% !important;
      margin: 16px 0 8px 0;
      color: #ffffff;
      font-weight: 400;
      font-size: 20px;
    }
  }

  .knowledge-progress__videoscounter-box {
    height: 100%;
  }

  .knowledge-progress__videoscounter {
    position: relative;
    top: 1.1em;
    left: -2em;
    text-align: right;
    color: rgba(0, 0, 0, 0.87);
    font-weight: bold;
    font-size: 0.8em;
  }
}
</style>
